import React, { useCallback, useEffect, useRef, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import makeRequest from "../../makeRequest";

function getYoutubeId(url) {
  const m = url.match(/[?&]v=([^&#]+)/) || url.match(/youtu\.be\/([^?#]+)/) || url.match(/embed\/([^?#]+)/) || url.match(/shorts\/([^?#]+)/);
  return m ? m[1] : null;
}

function VlogCard({ vlog }) {
  const vid = getYoutubeId(vlog.youtubeUrl || "");
  const embedUrl = vid ? `https://www.youtube.com/embed/${vid}` : null;
  const thumb = vid ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg` : null;

  return (
    <div className="col-xl-4 col-lg-4 col-md-6 mb-30">
      <div
        style={{
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
          background: "#fff",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {embedUrl ? (
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
            <iframe
              src={embedUrl}
              title={vlog.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </div>
        ) : thumb ? (
          <img src={thumb} alt={vlog.title} style={{ width: "100%", height: 200, objectFit: "cover" }} />
        ) : (
          <div
            style={{
              height: 200,
              background: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#9ca3af",
            }}
          >
            <i className="fa-solid fa-video" style={{ fontSize: 36 }} />
          </div>
        )}
        <div style={{ padding: "16px 18px 18px" }}>
          <h6
            style={{
              fontSize: "1rem",
              fontWeight: 600,
              marginBottom: 6,
              color: "#111827",
              lineHeight: 1.4,
            }}
          >
            {vlog.title}
          </h6>
          {vlog.description && (
            <p
              style={{
                fontSize: "0.85rem",
                color: "#6b7280",
                margin: 0,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {vlog.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const PER_PAGE = 12;

const VlogsMain = () => {
  const [vlogs, setVlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const fetchingRef = useRef(false);

  const fetchVlogs = useCallback(async (pageNum) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const data = await makeRequest({
        route: `/website/vlog/public?page=${pageNum}&perPage=${PER_PAGE}`,
        method: "get",
      });
      const items = data?.data || [];
      setVlogs((prev) => (pageNum === 1 ? items : [...prev, ...items]));
      const total = data?.totalItems || 0;
      setHasMore(pageNum * PER_PAGE < total);
      setPage(pageNum);
    } catch {
      // ignore
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchVlogs(1);
  }, [fetchVlogs]);

  useEffect(() => {
    if (!sentinelRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchVlogs(page + 1);
        }
      },
      { threshold: 0.1 },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, loading, page, fetchVlogs]);

  return (
    <main>
      <Breadcrumb title="Vlogs" />

      <div className="it-blog-area fix pt-120 pb-90">
        <div className="container">
          {vlogs.length === 0 && !loading ? (
            <div style={{ textAlign: "center", padding: 80, color: "#6b7280" }}>No vlogs available yet.</div>
          ) : (
            <div className="row">
              {vlogs.map((v) => (
                <VlogCard key={v.id} vlog={v} />
              ))}
            </div>
          )}

          {loading && (
            <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 36, color: "#076cec" }} />
            </div>
          )}

          {/* Sentinel for infinite scroll */}
          {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}

          {!hasMore && vlogs.length > 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "20px 0",
                color: "#9ca3af",
                fontSize: "0.85rem",
              }}
            >
              You've reached the end.
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default VlogsMain;
